import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import DataCard from '../components/page/DataCard';
import PageFilter from '../components/page/PageFilter';
import PageState from '../components/page/PageState';
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
/** Options passed to selectProject when opening a workspace task in the project workbench. */
function selectProjectOptionsForWorkspaceTask(task) {
  const base = {
    initialWorkspaceStep: task.workspaceStep,
    forceWorkbench: true,
  };
  if (task.section === "Risk Analysis" && task.workspaceStep === 6) {
    return { ...base, initialRiskAnalysisSubTab: "register" };
  }
  if (task.section === "Evidencing & Monitoring" && task.workspaceStep === 8) {
    const sub = String(task.subTab || "");
    if (sub.startsWith("Evidence")) {
      return { ...base, initialEvidencingSubTab: "evidence" };
    }
    if (sub.toLowerCase().includes("incident")) {
      return { ...base, initialEvidencingSubTab: "incidents" };
    }
  }
  return base;
}

/** Derive the "assigneeRole" check text so delegate / SPOC CI tasks are clickable. */

/** In-progress tasks are targeted at a role; use portal role only (one persona per email). */
function userCanOpenWorkspaceTask(user, _projects, task) {
    if (task.status !== 'in_progress') return false;
    if (!user?.role) return false;
    if (user.role === Role.ADMIN) return true;
    const r = (task.assigneeRole || '').toLowerCase();
    if (r.includes('spoc')) return user.role === Role.COMPANY_SPOC;
    if (r.includes('delegate')) return user.role === Role.COMPANY_DELEGATE;
    if (r.includes('lead advisor') && r.includes('/')) {
        return user.role === Role.LEAD_ADVISOR || user.role === Role.ADVISOR;
    }
    if (r.includes('lead advisor')) return user.role === Role.LEAD_ADVISOR;
    if (r.includes('advisor')) return user.role === Role.ADVISOR;
    return false;
}

/**
 * Global workspace tasks — derived from profiling / scoping / onboarding state (API).
 * Platform admins: no top-level Tasks in Control Plane; they open a project and use Tasks there (scoped to that project).
 */
const TaskCenter = () => {
    const { user, projects, selectProject, activeProject, fetchProjects, projectsLoading } = useApp();
    const isPlatformAdmin = user?.role === Role.ADMIN;
    /** Admin sees project-scoped tasks only when inside a project context */
    const adminScopedToProject = isPlatformAdmin && !!activeProject;
    const hideProjectColumn = adminScopedToProject;
    const { instance, accounts } = useMsal();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');

    const fetchTasks = useCallback(async () => {
        if (!accounts || accounts.length === 0) return;
        if (isPlatformAdmin && !activeProject) {
            setTasks([]);
            setError(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: [process.env.REACT_APP_API_SCOPE],
                account: accounts[0],
            });
            let url = `${process.env.REACT_APP_API_BASE_URL}/api/v1/workspace-tasks`;
            if (activeProject?.id) {
                url += `?projectId=${encodeURIComponent(activeProject.id)}`;
            }
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                setError(json.error?.message || res.statusText || 'Failed to load tasks');
                setTasks([]);
                return;
            }
            setTasks(json.data.tasks || []);
        } catch (e) {
            console.error(e);
            setError('Failed to load tasks');
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [instance, accounts, isPlatformAdmin, activeProject?.id]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const filtered = useMemo(() => {
        if (filter === 'all') return tasks;
        return tasks.filter((t) => t.status === filter);
    }, [tasks, filter]);

    const counts = useMemo(() => {
        const open = tasks.filter((t) => t.status === 'in_progress').length;
        const done = tasks.filter((t) => t.status === 'complete').length;
        return { open, done, total: tasks.length };
    }, [tasks]);

    const pendingTaskRef = React.useRef(null);

    const openProject = (task) => {
        const p = projects.find((pr) => pr.id === task.projectId);
        if (p) {
            pendingTaskRef.current = null;
            selectProject(p, selectProjectOptionsForWorkspaceTask(task));
            return;
        }
        // Project not in local cache yet — refresh and retry once loaded
        pendingTaskRef.current = task;
        fetchProjects();
    };

    useEffect(() => {
        if (!pendingTaskRef.current || projectsLoading) return;
        const task = pendingTaskRef.current;
        const p = projects.find((pr) => pr.id === task.projectId);
        if (p) {
            pendingTaskRef.current = null;
            selectProject(p, selectProjectOptionsForWorkspaceTask(task));
        }
    }, [projects, projectsLoading, selectProject]);

    const openWorkbenchForTask = (task) => {
        if (adminScopedToProject && activeProject && task.projectId === activeProject.id) {
            selectProject(activeProject, selectProjectOptionsForWorkspaceTask(task));
            return;
        }
        openProject(task);
    };

    const formatWhen = (iso) => {
        if (!iso) return '-';
        return formatDdMmYyyyTime(iso);
    };

    if (isPlatformAdmin && !activeProject) {
        return (
            <div className="max-w-3xl mx-auto space-y-8 py-16 px-4 animate-in fade-in duration-500">
                <h1 className="text-3xl md:text-4xl font-extrabold text-[color:var(--color-text-hero)] tracking-tight font-outfit">
                    Workspace <span className="text-[color:var(--color-accent)]">Tasks</span>
                </h1>
                <p className="text-[color:var(--color-text-muted)] text-lg leading-relaxed">
                    Platform admins view tasks <strong className="text-[color:var(--color-text-hero)]">inside each project</strong>. Go to{' '}
                    <strong className="text-[color:var(--color-text-hero)]">Control Plane</strong>, open a project from the list, then choose{' '}
                    <strong className="text-[color:var(--color-text-hero)]">Tasks</strong> in the sidebar for that engagement.
                </p>
            </div>
        );
    }

    const colCount = hideProjectColumn ? 6 : 7;

    return (
        <PageShell>
            <PageHeader
                title="Workspace Tasks"
                subtitle={adminScopedToProject ? (
                    <>
                        Onboarding workflow for <strong>{activeProject?.name}</strong>
                        {' - '}
                        <strong>{user?.name}</strong>
                    </>
                ) : (
                    <>
                        Onboarding workflow across all projects - <strong>{user?.name}</strong>
                    </>
                )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <DataCard>
                    <div className="text-[10px] font-black text-[color:var(--color-text-muted)] uppercase tracking-widest mb-1">In progress</div>
                    <div className="text-3xl font-black text-[color:var(--color-accent)] font-outfit">{loading ? '-' : counts.open}</div>
                </DataCard>
                <DataCard>
                    <div className="text-[10px] font-black text-[color:var(--color-text-muted)] uppercase tracking-widest mb-1">Complete</div>
                    <div className="text-3xl font-black text-[color:var(--color-ok-fg)] font-outfit">{loading ? '-' : counts.done}</div>
                </DataCard>
                <DataCard>
                    <div className="text-[10px] font-black text-[color:var(--color-text-muted)] uppercase tracking-widest mb-1">Total rows</div>
                    <div className="text-3xl font-black text-[color:var(--color-text-hero)] font-outfit">{loading ? '-' : counts.total}</div>
                </DataCard>
            </div>

            <DataCard className="overflow-hidden" styles={{ body: { padding: 0 } }}>
                <div className="px-8 md:px-10 py-8 border-b border-[color:var(--color-border)] flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)]">
                    <h2 className="text-2xl font-black text-[color:var(--color-text-hero)] tracking-tight font-outfit">Task registry</h2>
                    <PageFilter
                        value={filter}
                        onChange={setFilter}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'in_progress', label: 'In progress' },
                            { value: 'complete', label: 'Complete' },
                        ]}
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left min-w-[900px]">
                        <thead>
                            <tr className="text-[10px] font-extrabold text-[color:var(--color-text-muted)] uppercase tracking-widest border-b border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_10%,transparent)]">
                                {!hideProjectColumn && <th className="px-8 md:px-10 py-5">Project</th>}
                                <th className="px-8 md:px-10 py-5">Section</th>
                                <th className="px-8 md:px-10 py-5">Sub-tab</th>
                                <th className="px-8 md:px-10 py-5">Allotted</th>
                                <th className="px-8 md:px-10 py-5">Assignee</th>
                                <th className="px-8 md:px-10 py-5">Status</th>
                                <th className="px-8 md:px-10 py-5 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[color:var(--color-border)]">
                            {loading ? (
                                <tr>
                                    <td colSpan={colCount}>
                                        <PageState status="loading" message="Loading tasks…" />
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={colCount}>
                                        <PageState status="error" message={error} />
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={colCount}>
                                        <PageState status="empty" message="No tasks match this filter." />
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((task) => (
                                    <tr key={task.id} className="hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_20%,transparent)] transition-colors group">
                                        {!hideProjectColumn && (
                                            <td className="px-8 md:px-10 py-6 align-top">
                                                <div className="font-black text-[color:var(--color-text-hero)] text-sm group-hover:text-[color:var(--color-accent)] transition-colors">
                                                    {task.projectName}
                                                </div>
                                                <div className="text-[10px] text-[color:var(--color-text-muted)] font-bold uppercase tracking-wide mt-1">
                                                    {task.companyName}
                                                </div>
                                            </td>
                                        )}
                                        <td className="px-8 md:px-10 py-6 align-top text-sm font-semibold text-[color:var(--color-text-hero)]">
                                            {task.section}
                                        </td>
                                        <td className="px-8 md:px-10 py-6 align-top text-sm text-[color:var(--color-text-muted)]">{task.subTab}</td>
                                        <td className="px-8 md:px-10 py-6 align-top text-sm text-[color:var(--color-text-muted)] whitespace-nowrap">
                                            {formatWhen(task.allottedAt)}
                                        </td>
                                        <td className="px-8 md:px-10 py-6 align-top text-sm text-[color:var(--color-text-hero)]">{task.assigneeRole}</td>
                                        <td className="px-8 md:px-10 py-6 align-top">
                                            <span
                                                className={`inline-flex px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                                                    task.status === 'complete'
                                                        ? 'bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]'
                                                        : 'bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]'
                                                }`}
                                            >
                                                {task.status === 'complete' ? 'Complete' : 'In progress'}
                                            </span>
                                        </td>
                                        <td className="px-8 md:px-10 py-6 align-top text-right">
                                            {task.status === 'in_progress' && userCanOpenWorkspaceTask(user, projects, task) ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openWorkbenchForTask(task)}
                                                    className="px-5 py-2.5 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm"
                                                >
                                                    {adminScopedToProject ? 'Open workbench' : 'Open project'}
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-[color:var(--color-text-muted)] font-bold uppercase tracking-widest">
                                                    -
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </DataCard>
        </PageShell>
    );
};

export default TaskCenter;
